import { forwardRef, useId } from "react";
import type { ReactNode, TextareaHTMLAttributes } from "react";
import "./TextArea.css";

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  helperText?: ReactNode;
  errorText?: ReactNode;
  invalid?: boolean;
  resize?: "none" | "vertical" | "horizontal" | "both";
}

const cx = (...classes: Array<string | false | undefined>) => classes.filter(Boolean).join(" ");

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  {
    label,
    helperText,
    errorText,
    invalid,
    resize = "vertical",
    id,
    className,
    rows = 4,
    disabled,
    "aria-describedby": ariaDescribedBy,
    ...rest
  },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? `go4-textarea-${autoId}`;
  const helperId = `${inputId}-helper`;
  const errorId = `${inputId}-error`;
  const isInvalid = invalid || Boolean(errorText);

  const describedBy =
    [ariaDescribedBy, errorText ? errorId : null, helperText ? helperId : null]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <div className={cx("go4-field", disabled && "go4-field--disabled", className)}>
      {label ? (
        <label htmlFor={inputId} className="go4-field__label">
          {label}
        </label>
      ) : null}
      <textarea
        ref={ref}
        id={inputId}
        rows={rows}
        disabled={disabled}
        aria-invalid={isInvalid || undefined}
        aria-describedby={describedBy}
        data-resize={resize}
        className={cx("go4-textarea", isInvalid && "go4-textarea--invalid")}
        {...rest}
      />
      {errorText ? (
        <p id={errorId} className="go4-field__error" role="alert">
          {errorText}
        </p>
      ) : helperText ? (
        <p id={helperId} className="go4-field__helper">
          {helperText}
        </p>
      ) : null}
    </div>
  );
});
